"use client";

import { useMemo, useState, useSyncExternalStore } from "react";
import { RecitalAudience } from "@/components/live/recital-audience";
import type { LiveParticipantLine } from "@/lib/live/session-control";
import { ChatWidget, type ChatAuthor, type ChatMessage, type ChatModeration } from "./chat-widget";

/**
 * Banc du widget de chat, en dev seulement : `/studio?widget=chat`. Rien n'est
 * branché — un fil simulé écrit toutes les quelques secondes, et ce qu'on envoie
 * revient par le même fil, comme la salle le rendrait.
 *
 * `/studio?widget=chat-mod` : le même, vu de l'interprète — le bouton qui
 * ferme le chat, le menu sur chaque ligne, la marque « sourdine » ; chaque
 * geste s'applique ici, comme la salle le rendrait.
 */
export function ChatWidgetDemo({ moderating = false }: { moderating?: boolean }) {
  const messages = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [closed, setClosed] = useState(false);
  const [muted, setMuted] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [gone, setGone] = useState<ReadonlySet<string>>(new Set());

  const moderation = useMemo<ChatModeration | undefined>(
    () =>
      moderating
        ? {
            setClosed: async (next) => {
              await settle();
              setClosed(next);

              return true;
            },
            retract: async (id) => {
              await settle();
              retract(id);

              return true;
            },
            mute: async (identity, next) => {
              await settle();
              setMuted((held) => new Map(held).set(identity, next));

              return true;
            },
            exclude: async (identity) => {
              await settle();
              setGone((held) => new Set(held).add(identity));

              return true;
            },
          }
        : undefined,
    [moderating],
  );

  const self = moderating ? PERFORMER : SELF;
  const lines = LINES.filter((line) => !gone.has(line.identity)).map((line) => ({
    ...line,
    muted: muted.get(line.identity) ?? false,
  }));
  const shown = messages
    .filter((message) => !gone.has(message.author.id))
    .map((message) => ({ ...message, author: { ...message.author, muted: muted.get(message.author.id) ?? false } }));

  return (
    <ChatWidget
      messages={shown}
      selfId={self.id}
      onSend={send}
      audience={43}
      closed={closed}
      moderation={moderation}
      aside={
        <RecitalAudience
          identity={self.id}
          lines={lines}
          headcount={43}
          paused={false}
          moderation={
            moderation === undefined
              ? undefined
              : { gestures: { mute: moderation.mute, exclude: moderation.exclude }, mutedOverride: muted }
          }
        />
      }
    />
  );
}

function settle(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 150));
}

function retract(id: string): void {
  messages = messages.filter((message) => message.id !== id);

  for (const listener of listeners) {
    listener();
  }
}

const SELF: ChatAuthor = { id: "self", name: "Toi", role: "spectator" };
const PERFORMER: ChatAuthor = { id: "performer", name: "Camille Laurent", role: "performer" };
const SPECTATORS: readonly ChatAuthor[] = [
  { id: "lea", name: "Léa", role: "spectator" },
  { id: "hugo", name: "Hugo M.", role: "spectator" },
  { id: "ines", name: "Inès", role: "spectator" },
  { id: "marc", name: "Marc", role: "spectator" },
  { id: "sofia", name: "Sofia", role: "spectator" },
];

const LINES: readonly LiveParticipantLine[] = [
  { identity: PERFORMER.id, name: PERFORMER.name, canPlay: true, measures: null, muted: false },
  { identity: SELF.id, name: SELF.name, canPlay: false, measures: null, muted: false },
  ...SPECTATORS.map((who) => ({ identity: who.id, name: who.name, canPlay: false, measures: null, muted: false })),
];

const SCRIPT: readonly (readonly [ChatAuthor, string])[] = [
  [SPECTATORS[3], "Le passage à la main gauche est superbe"],
  [SPECTATORS[1], "On voit super bien les accords sur le roll"],
  [PERFORMER, "Prochain morceau dans deux minutes, le temps de souffler"],
  [SPECTATORS[4], "Tu peux rejouer le début ? 🙏"],
  [SPECTATORS[2], "Je découvre, c'est génial ce format"],
  [SPECTATORS[0], "La nuance au milieu, wow"],
  [SPECTATORS[3], "Quelqu'un sait quel est le prochain ?"],
  [PERFORMER, "Une Gymnopédie, pour finir en douceur"],
];

const HELLO_AT = Date.now();

let messages: readonly ChatMessage[] = [
  { id: "seed-1", author: PERFORMER, text: "Bonsoir à tous, merci d'être là !", at: HELLO_AT },
  { id: "seed-2", author: SPECTATORS[0], text: "Bonsoir Camille 👋", at: HELLO_AT },
  { id: "seed-3", author: SPECTATORS[2], text: "Hâte d'entendre ça", at: HELLO_AT },
];
let cursor = 0;
let timer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function append(author: ChatAuthor, text: string): void {
  messages = [...messages, { id: `m-${messages.length}-${Date.now()}`, author, text, at: Date.now() }];

  for (const listener of listeners) {
    listener();
  }
}

// Comme la salle : le message revient un peu plus tard, et « trop vite » se
// refuse pour éprouver le mot d'erreur.
async function send(text: string): Promise<"sent" | "too-fast" | "failed"> {
  if (text === "vite") {
    return "too-fast";
  }

  await new Promise((resolve) => setTimeout(resolve, 150));
  append(SELF, text);

  return "sent";
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  if (timer === null) {
    timer = setInterval(() => {
      const [author, text] = SCRIPT[cursor % SCRIPT.length];
      cursor += 1;
      append(author, text);
    }, 2600);
  }

  return () => {
    listeners.delete(listener);

    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

function getSnapshot(): readonly ChatMessage[] {
  return messages;
}
